import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { api } from '../../lib/api';
import { formatPrice } from '../../lib/utils';

const STATUS_STYLES = {
  offen: 'bg-amber-50 text-amber-800 border-amber-200',
  bezahlt: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  überfällig: 'bg-red-50 text-red-800 border-red-200',
  storniert: 'bg-slate-100 text-slate-600 border-slate-200',
};

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [form, setForm] = useState({ provider_id: '', amount_net: '390', period_start: '', period_end: '', description: 'Jahresabo Pro' });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [inv, acc, prov] = await Promise.all([api.getInvoices(), api.getProviderAccounts(), api.getProviders()]);
      setInvoices(inv);
      setAccounts(acc);
      setProviders(prov);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const createInvoice = async (e) => {
    e.preventDefault();
    if (!form.provider_id) { toast.error('Bitte Provider wählen'); return; }
    if (!form.period_start || !form.period_end) { toast.error('Zeitraum angeben'); return; }
    try {
      await api.createInvoice({ ...form, amount_net: parseFloat(form.amount_net) });
      toast.success('Rechnung erstellt');
      setForm({ provider_id: '', amount_net: '390', period_start: '', period_end: '', description: 'Jahresabo Pro' });
      loadAll();
    } catch (err) { toast.error(err.message); }
  };

  const markPaid = async (id) => {
    try {
      await api.markInvoicePaid(id);
      toast.success('Rechnung als bezahlt markiert – Provider ist jetzt Pro');
      loadAll();
    } catch (err) { toast.error(err.message); }
  };

  const openPdf = async (id) => {
    try {
      const html = await api.getInvoicePdf(id);
      const win = window.open('', '_blank');
      if (!win) { toast.error('Popup wurde blockiert – bitte erlauben'); return; }
      win.document.write(html);
      win.document.close();
    } catch (err) { toast.error(err.message); }
  };

  const approve = async (acc) => {
    try {
      await api.approveProviderAccount(acc.id);
      toast.success('Konto freigeschaltet');
      loadAll();
    } catch (err) { toast.error(err.message); }
  };

  const block = async (acc) => {
    try {
      await api.blockProviderAccount(acc.id);
      toast.success('Konto gesperrt');
      loadAll();
    } catch (err) { toast.error(err.message); }
  };

  const setTier = async (acc, tier) => {
    try {
      await api.setProviderTier(acc.id, tier);
      toast.success(`Tier auf ${tier} gesetzt`);
      loadAll();
    } catch (err) { toast.error(err.message); }
  };

  const runRecurring = async () => {
    try {
      const res = await api.runRecurring();
      toast.success(`Downgrades: ${res.downgraded.length}, Verlängerungen: ${res.renewals.length}`);
      loadAll();
    } catch (err) { toast.error(err.message); }
  };

  const pendingAccounts = accounts.filter(a => a.status === 'pending' || a.status === 'claim_pending');

  return (
    <div className="space-y-6">
      {/* Provider-Anfragen */}
      <Card className="rounded-2xl">
        <CardContent className="p-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Provider-Anfragen & Freischaltungen</h2>
          {pendingAccounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">Keine offenen Anfragen.</p>
          ) : (
            <div className="space-y-3">
              {pendingAccounts.map(acc => {
                const provider = acc.provider_id ? providers.find(p => p.id === acc.provider_id) : null;
                return (
                  <div key={acc.id} className="flex flex-wrap items-center gap-3 border border-slate-200 rounded-lg p-3">
                    <div className="flex-1 min-w-[200px]">
                      <p className="font-medium text-sm text-slate-900">{acc.company_name || acc.email}</p>
                      <p className="text-xs text-muted-foreground">{acc.email}</p>
                      {provider && (
                        <p className="text-xs text-sky-600 mt-0.5">
                          Claim: {provider.name} (bestehende Preisdaten werden übernommen)
                        </p>
                      )}
                      {!provider && <p className="text-xs text-amber-600 mt-0.5">Neuer Provider (wird angelegt)</p>}
                    </div>
                    <Badge variant="outline" className="text-[10px]">
                      {acc.status === 'claim_pending' ? 'Claim-Prüfung' : 'Neu'}
                    </Badge>
                    <Button size="sm" onClick={() => approve(acc)}>Freischalten</Button>
                    <Button size="sm" variant="outline" onClick={() => block(acc)}>Ablehnen</Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alle Accounts */}
      <Card className="rounded-2xl">
        <CardContent className="p-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Alle Provider-Accounts</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Firma</th>
                  <th className="py-2 pr-3">E-Mail</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Tier</th>
                  <th className="py-2">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map(acc => {
                  const provider = acc.provider_id ? providers.find(p => p.id === acc.provider_id) : null;
                  return (
                    <tr key={acc.id} className="border-b border-slate-50">
                      <td className="py-2 pr-3 font-medium">{acc.company_name || '–'}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{acc.email}</td>
                      <td className="py-2 pr-3">
                        <Badge variant="outline" className="text-[10px]">{acc.status}</Badge>
                      </td>
                      <td className="py-2 pr-3">
                        {provider ? (
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="text-[10px]">{provider.tier}</Badge>
                            {provider.tier === 'basic' && (
                              <Button size="sm" variant="ghost" className="text-[10px] h-6" onClick={() => setTier(acc, 'pro')}>→ Pro</Button>
                            )}
                            {provider.tier === 'pro' && (
                              <Button size="sm" variant="ghost" className="text-[10px] h-6" onClick={() => setTier(acc, 'basic')}>→ Basic</Button>
                            )}
                          </div>
                        ) : '–'}
                      </td>
                      <td className="py-2">
                        <Button size="sm" variant="outline" onClick={() => block(acc)}>Sperren</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Neue Rechnung */}
      <Card className="rounded-2xl">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Neue Rechnung (Jahresabo Pro, 390 € netto)</h2>
            <Button variant="outline" size="sm" onClick={runRecurring}>Recurring-Job ausführen</Button>
          </div>
          <form onSubmit={createInvoice} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>Provider</Label>
              <select className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm" value={form.provider_id} onChange={e => setForm({...form, provider_id: e.target.value})}>
                <option value="">Bitte wählen...</option>
                {providers.map(p => <option key={p.id} value={p.id}>{p.name} ({p.tier})</option>)}
              </select>
            </div>
            <div><Label>Netto (€)</Label><Input type="number" step="0.01" value={form.amount_net} onChange={e => setForm({...form, amount_net: e.target.value})} /></div>
            <div><Label>Zeitraum von</Label><Input type="date" value={form.period_start} onChange={e => setForm({...form, period_start: e.target.value})} /></div>
            <div><Label>Zeitraum bis</Label><Input type="date" value={form.period_end} onChange={e => setForm({...form, period_end: e.target.value})} /></div>
            <div className="sm:col-span-2 lg:col-span-4 flex items-end">
              <Button type="submit">Rechnung erstellen</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Rechnungsliste */}
      <Card className="rounded-2xl">
        <CardContent className="p-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Rechnungen</h2>
          {invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Rechnungen.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">Nummer</th>
                    <th className="py-2 pr-3">Provider</th>
                    <th className="py-2 pr-3">Brutto</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Fällig</th>
                    <th className="py-2">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => {
                    const provider = providers.find(p => p.id === inv.provider_id);
                    return (
                      <tr key={inv.id} className="border-b border-slate-50">
                        <td className="py-2 pr-3 font-medium">{inv.number}</td>
                        <td className="py-2 pr-3">{provider?.name || inv.provider_id}</td>
                        <td className="py-2 pr-3">{formatPrice(inv.amount_gross || inv.amount_net)}</td>
                        <td className="py-2 pr-3">
                          <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[inv.status] || ''}`}>{inv.status}</Badge>
                        </td>
                        <td className="py-2 pr-3 text-muted-foreground">{inv.due_date}</td>
                        <td className="py-2 flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => openPdf(inv.id)}>PDF</Button>
                          {inv.status !== 'bezahlt' && (
                            <Button size="sm" onClick={() => markPaid(inv.id)}>Bezahlt ✓</Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
